import { supabase } from './supabaseService';

export type BullhornMappingEntity = 'vacancy' | 'candidate';
export type SyncDirection = 'inbound' | 'outbound' | 'both';

export interface FieldMappingRule {
  id: string;
  werftoolsField: string;
  bullhornField: string;
  direction: SyncDirection;
  note?: string;
}

export interface MappingChangeLogEntry {
  at: string;
  entity: BullhornMappingEntity;
  message: string;
  werftoolsField?: string;
  bullhornField?: string;
}

export interface BullhornFieldMappingsConfig {
  vacancy: FieldMappingRule[];
  candidate: FieldMappingRule[];
  mappingChangeLog?: MappingChangeLogEntry[];
}

export interface EntityFieldDef {
  key: string;
  label: string;
  type?: string;
}

export const ENTITY_LABELS: Record<BullhornMappingEntity, { werftools: string; bullhorn: string }> = {
  vacancy: { werftools: 'vacancies', bullhorn: 'JobOrder' },
  candidate: { werftools: 'candidates', bullhorn: 'Candidate' },
};

export const WERFTOOLS_FIELDS: Record<BullhornMappingEntity, EntityFieldDef[]> = {
  vacancy: [
    { key: 'title', label: 'Titel', type: 'text' },
    { key: 'description', label: 'Omschrijving', type: 'text' },
    { key: 'status', label: 'Status (Open/Afgerond)', type: 'enum' },
    { key: 'bullhorn_id', label: 'Bullhorn ID', type: 'id' },
    { key: 'vacancy_number', label: 'Vacaturenummer', type: 'number' },
    { key: 'opdrachtgever_id', label: 'Opdrachtgever (CRM)', type: 'relation' },
    { key: 'eindklant_id', label: 'Eindklant (CRM)', type: 'relation' },
    { key: 'data.bullhorn.status', label: 'Ruwe BH-status', type: 'json' },
    { key: 'data.bullhorn.dateAdded', label: 'Aangemaakt in BH', type: 'date' },
    { key: 'data.duration', label: 'Duur', type: 'text' },
    { key: 'data.bullhorn.salary', label: 'Salaris (data)', type: 'json' },
  ],
  candidate: [
    { key: 'name', label: 'Naam', type: 'text' },
    { key: 'phone', label: 'Telefoon', type: 'text' },
    { key: 'candidate_number', label: 'Kandidaatnummer', type: 'number' },
    { key: 'bullhorn_id', label: 'Bullhorn ID', type: 'id' },
    { key: 'skill_set', label: 'Skills', type: 'array' },
    { key: 'original_cv_text', label: 'CV / samenvatting', type: 'text' },
    { key: 'address_city', label: 'Plaats', type: 'text' },
    { key: 'address_zip', label: 'Postcode', type: 'text' },
    { key: 'address_country', label: 'Land', type: 'text' },
    { key: 'status', label: 'Status', type: 'enum' },
    { key: 'source', label: 'Bron', type: 'text' },
    { key: 'ai_parsed_data', label: 'AI-profiel', type: 'json' },
  ],
};

export const BULLHORN_FIELDS: Record<BullhornMappingEntity, EntityFieldDef[]> = {
  vacancy: [
    { key: 'title', label: 'title', type: 'text' },
    { key: 'description', label: 'description', type: 'text' },
    { key: 'publicDescription', label: 'publicDescription', type: 'text' },
    { key: 'status', label: 'status', type: 'enum' },
    { key: 'id', label: 'id', type: 'id' },
    { key: 'dateAdded', label: 'dateAdded', type: 'date' },
    { key: 'clientCorporation.id', label: 'clientCorporation.id', type: 'relation' },
    { key: 'clientCorporation.name', label: 'clientCorporation.name', type: 'text' },
    { key: 'clientContact.id', label: 'clientContact.id', type: 'relation' },
    { key: 'durationWeeks', label: 'durationWeeks', type: 'number' },
    { key: 'salary', label: 'salary', type: 'number' },
    { key: 'employmentType', label: 'employmentType', type: 'text' },
    { key: 'isOpen', label: 'isOpen', type: 'boolean' },
  ],
  candidate: [
    { key: 'firstName', label: 'firstName', type: 'text' },
    { key: 'lastName', label: 'lastName', type: 'text' },
    { key: 'email', label: 'email', type: 'text' },
    { key: 'email2', label: 'email2', type: 'text' },
    { key: 'phone', label: 'phone', type: 'text' },
    { key: 'mobile', label: 'mobile', type: 'text' },
    { key: 'id', label: 'id', type: 'id' },
    { key: 'status', label: 'status', type: 'enum' },
    { key: 'skillSet', label: 'skillSet', type: 'text' },
    { key: 'description', label: 'description', type: 'text' },
    { key: 'address.city', label: 'address.city', type: 'text' },
    { key: 'address.zip', label: 'address.zip', type: 'text' },
    { key: 'address.countryName', label: 'address.countryName', type: 'text' },
    { key: 'dateAdded', label: 'dateAdded', type: 'date' },
    { key: 'source', label: 'source', type: 'text' },
  ],
};

const newId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `map-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const DEFAULT_FIELD_MAPPINGS: BullhornFieldMappingsConfig = {
  vacancy: [
    { id: newId(), werftoolsField: 'title', bullhornField: 'title', direction: 'both' },
    { id: newId(), werftoolsField: 'description', bullhornField: 'description', direction: 'both' },
    {
      id: newId(),
      werftoolsField: 'status',
      bullhornField: 'status',
      direction: 'inbound',
      note: 'BH-status → Open/Afgerond',
    },
    { id: newId(), werftoolsField: 'bullhorn_id', bullhornField: 'id', direction: 'both' },
    { id: newId(), werftoolsField: 'vacancy_number', bullhornField: 'id', direction: 'inbound' },
    {
      id: newId(),
      werftoolsField: 'eindklant_id',
      bullhornField: 'clientCorporation.id',
      direction: 'inbound',
      note: 'via CRM-bedrijf',
    },
    {
      id: newId(),
      werftoolsField: 'opdrachtgever_id',
      bullhornField: 'clientContact.id',
      direction: 'inbound',
      note: 'via CRM-contact',
    },
    { id: newId(), werftoolsField: 'data.bullhorn.dateAdded', bullhornField: 'dateAdded', direction: 'inbound' },
    { id: newId(), werftoolsField: 'data.duration', bullhornField: 'durationWeeks', direction: 'inbound' },
  ],
  candidate: [
    {
      id: newId(),
      werftoolsField: 'name',
      bullhornField: 'firstName',
      direction: 'both',
      note: '+ lastName',
    },
    { id: newId(), werftoolsField: 'phone', bullhornField: 'phone', direction: 'both' },
    { id: newId(), werftoolsField: 'phone', bullhornField: 'mobile', direction: 'inbound', note: 'fallback' },
    { id: newId(), werftoolsField: 'candidate_number', bullhornField: 'id', direction: 'inbound' },
    { id: newId(), werftoolsField: 'bullhorn_id', bullhornField: 'id', direction: 'both' },
    { id: newId(), werftoolsField: 'skill_set', bullhornField: 'skillSet', direction: 'both' },
    { id: newId(), werftoolsField: 'original_cv_text', bullhornField: 'description', direction: 'both' },
    { id: newId(), werftoolsField: 'address_city', bullhornField: 'address.city', direction: 'both' },
    { id: newId(), werftoolsField: 'address_zip', bullhornField: 'address.zip', direction: 'both' },
    { id: newId(), werftoolsField: 'address_country', bullhornField: 'address.countryName', direction: 'both' },
  ],
  mappingChangeLog: [],
};

export const DIRECTION_LABELS: Record<SyncDirection, string> = {
  inbound: 'BH → Werftools',
  outbound: 'Werftools → BH',
  both: '2-weg',
};

export function cycleDirection(d: SyncDirection): SyncDirection {
  if (d === 'both') return 'inbound';
  if (d === 'inbound') return 'outbound';
  return 'both';
}

export function mergeMappings(
  saved: Partial<BullhornFieldMappingsConfig> | null | undefined
): BullhornFieldMappingsConfig {
  return {
    vacancy:
      saved?.vacancy?.length ? saved.vacancy.map((r) => ({ ...r, id: r.id || newId() })) : DEFAULT_FIELD_MAPPINGS.vacancy,
    candidate:
      saved?.candidate?.length
        ? saved.candidate.map((r) => ({ ...r, id: r.id || newId() }))
        : DEFAULT_FIELD_MAPPINGS.candidate,
    mappingChangeLog: saved?.mappingChangeLog ?? [],
  };
}

export async function loadBullhornFieldMappings(clientId: string): Promise<BullhornFieldMappingsConfig> {
  const { data, error } = await supabase
    .from('clients')
    .select('bullhorn_settings')
    .eq('id', clientId)
    .single();
  if (error) throw error;
  const settings = data?.bullhorn_settings as Record<string, unknown> | null;
  return mergeMappings(settings?.fieldMappings as BullhornFieldMappingsConfig | undefined);
}

export async function saveBullhornFieldMappings(
  clientId: string,
  mappings: BullhornFieldMappingsConfig,
  existingSettings: Record<string, unknown> | null
): Promise<void> {
  const trimmedLog = (mappings.mappingChangeLog || []).slice(0, 200);

  const nextSettings = {
    ...(existingSettings || {}),
    fieldMappings: {
      vacancy: mappings.vacancy,
      candidate: mappings.candidate,
      mappingChangeLog: trimmedLog,
    },
  };

  const { error } = await supabase
    .from('clients')
    .update({ bullhorn_settings: nextSettings })
    .eq('id', clientId);
  if (error) throw error;
}

export function fieldLabel(entity: BullhornMappingEntity, side: 'werftools' | 'bullhorn', key: string): string {
  const list = side === 'werftools' ? WERFTOOLS_FIELDS[entity] : BULLHORN_FIELDS[entity];
  return list.find((f) => f.key === key)?.label || key;
}
